import React from "react";
import { Users2, Megaphone, Layers, Lock, UserPlus, MapPin, Calendar, BookOpen, ShieldCheck, MessageSquare, BellRing, Archive } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, StatBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { ProfileFieldsMock, TeamRolesMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1553028826-f4804a6dba3b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function GroupsFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-groups-page">
      <FeatureHero
        eyebrow="Groups"
        icon={Users2}
        title="Where the community actually gathers."
        copy="Groups give founders, operators, investors, and organizers a shared home — with the streams, members, announcements, and conversations that keep a community moving between events."
        ctaLabel="Explore Groups"
        ctaTo="/groups"
        visual={<Shot src="/features/group-page.png" alt="The Startup Otter group page for Front Range Founders, with cover image, organizers and streams" url="startupotter.com/g/frontrangefounders" testId="groups-hero-visual" priority />}
      />

      <FeatureRow
        testId="groups-create"
        label="Create"
        icon={Users2}
        title="Give your community a real home."
        copy="Name it, claim a handle, describe who it's for, and decide how people get in. Your group gets a page of its own that members and newcomers can actually find."
        visual={<ProfileFieldsMock title="Group details" testId="mock-group-details" items={[["Handle", "@frontrangefounders"], ["Category", "Founders"], ["Where", "Denver, CO, US · Local"], ["Joining", "Anyone can join"], ["Topics", "Fundraising · Go-to-market"]]} />}
      />

      <FeatureRow
        testId="groups-streams"
        tone="dark"
        flip
        label="Streams"
        icon={Layers}
        title="One group, separate conversations."
        copy="Streams keep a busy community organized — announcements in one place, introductions in another, and focused threads for the topics your members care about most."
        visual={<Shot src="/features/group-page.png" alt="Streams inside a Startup Otter group, with announcements and topic streams in the sidebar" url="startupotter.com/g/frontrangefounders" dark cropClass="h-[420px] object-bottom" testId="groups-streams-shot" />}
      />

      <PhotoBand
        testId="groups-community"
        src={PHOTO}
        alt="Members of a startup community working together around a table"
        kicker="Why Groups"
        title="Communities are built between the events."
        copy="A meetup ends and the momentum usually goes with it. Groups keep the room together afterwards — the introductions, the follow-ups, the asks and offers that turn a crowd into a community."
      />

      <FeatureRow
        testId="groups-members"
        tone="tint"
        label="Members"
        icon={UserPlus}
        title="See who's in the room."
        copy="Every member is a real Personal Profile, so the directory shows what people actually do, where they are, and what they know — and a conversation is one click away."
        visual={<Shot src="/features/group-members.png" alt="The member directory inside a Startup Otter group, showing profiles with roles and headlines" url="startupotter.com/g/frontrangefounders" testId="groups-members-shot" />}
      />

      <IconGrid
        testId="groups-inside"
        tone="brand"
        columns={3}
        title="Everything a community needs in one place."
        items={[
          { icon: Megaphone, title: "Announcements.", copy: "Reach every member when it matters, with an announcement stream organizers control." },
          { icon: MessageSquare, title: "Member messaging.", copy: "Private conversations between members inside the group, without trading contact details first." },
          { icon: BellRing, title: "Notifications your way.", copy: "Each member chooses All activity, Important only, Mentions only, or Muted — per group." },
        ]}
      />

      <FeatureRow
        testId="groups-discovery"
        label="Discovery"
        icon={MapPin}
        title="Found by the people who belong there."
        copy="Groups are discoverable by category, industry, topic, membership type and distance — and Local Mode brings the ones near you to the surface."
        visual={<Shot src="/features/groups-directory.png" alt="Browsing Startup Otter groups with filters for category, industry, topics and distance" url="startupotter.com/groups" testId="groups-directory-shot" />}
      />

      <FeatureRow
        testId="groups-roles"
        tone="tint"
        flip
        label="Organizers"
        icon={ShieldCheck}
        title="Run it with the people you trust."
        copy="Owners and Admins share the work — approving requests, moderating content, posting announcements and keeping the group healthy — with a clear record of who did what."
        visual={<TeamRolesMock testId="mock-group-roles" />}
      />

      <IconGrid
        testId="groups-connected"
        tone="tint"
        columns={3}
        title="Connected to the rest of Startup Otter."
        items={[
          { icon: Calendar, title: "Events.", copy: "Share the gatherings your community is hosting or attending, right alongside the conversation." },
          { icon: BookOpen, title: "Resources.", copy: "Keep the tools, programs and knowledge your members rely on attached to the group." },
          { icon: Lock, title: "Private by design.", copy: "Posts inside a group stay inside it unless an author chooses to share with the wider community." },
        ]}
      />

      <StatBand
        testId="groups-stats"
        tone="dark"
        items={[
          { value: "Open", label: "Anyone can join and start taking part immediately." },
          { value: "Approval", label: "Organizers review requests, with questions if you want them." },
          { value: "Invite only", label: "Membership strictly by invitation from the people who run it." },
        ]}
      />

      <FeatureRow
        testId="groups-lifecycle"
        label="Built to last"
        icon={Archive}
        title="Your community, on your terms."
        copy="Groups can be archived and brought back whenever you're ready, ownership can be handed to the next organizer, and deletion gives you a 30-day window to change your mind."
        visual={<ProfileFieldsMock title="Organizer controls" testId="mock-group-lifecycle" items={[["Ownership", "Transfer to another organizer"], ["Archive", "Reversible, keeps everything"], ["Deletion", "30 days to restore"], ["Audit", "A record of every admin action"]]} />}
      />

      <ClosingCta
        testId="groups-closing"
        title="Find your people. Then keep them together."
        ctaLabel="Explore Groups"
        ctaTo="/groups"
      />

      <FeatureFooterNav next={{ to: "/features/personal-profiles", label: "Personal Profiles", desc: "Show who you are, what you know, and what you bring to the ecosystem." }} />
    </div>
  );
}
